import React from 'react';

/**
 * One-time AI data disclosure consent — shown before a user's first
 * message is sent to Care Copilot, in either the floating widget
 * (CareCopilotWidget.jsx) or the full-page version (CareCopilot.jsx).
 *
 * Satisfies App Store Guidelines 5.1.1(i)/5.1.2(i): the app must
 * disclose what data is sent, identify who it's sent to, and obtain
 * the user's permission before sharing personal data with a
 * third-party AI service.
 *
 * Persistence: a "accepted" flag is stored in localStorage under
 * AI_DISCLOSURE_STORAGE_KEY (exported below) so both CareCopilotWidget
 * and CareCopilot share the same acceptance state — accepting in one
 * place means it won't ask again in the other. This is a one-time,
 * per-browser disclosure, not tied to the backend/account — if the
 * same person signs in on a different device, they'll see it again
 * once there, which is expected and fine for a disclosure requirement.
 */

export const AI_DISCLOSURE_STORAGE_KEY = 'ac_ai_disclosure_accepted_v1';

export function hasAcceptedAIDisclosure() {
  try {
    return localStorage.getItem(AI_DISCLOSURE_STORAGE_KEY) === 'true';
  } catch {
    // localStorage can throw in some restricted/private-browsing contexts —
    // fail safe by treating it as not-yet-accepted, so the disclosure
    // still shows rather than silently skipping it.
    return false;
  }
}

export function recordAIDisclosureAcceptance() {
  try {
    localStorage.setItem(AI_DISCLOSURE_STORAGE_KEY, 'true');
  } catch {
    // If storage isn't available, the modal will simply reappear next
    // time — acceptable degradation, not a functional blocker.
  }
}

export default function AIDisclosureModal({ onAccept, onDecline }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        background: 'rgba(0,0,0,0.9)',
        borderRadius: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        zIndex: 20,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '340px' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>🤖</div>
        <div style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '10px' }}>
          Before You Chat with Care Copilot
        </div>
        <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: '10px', textAlign: 'left' }}>
          Care Copilot uses <strong>Claude</strong>, an AI service by Anthropic, delivered securely through{' '}
          <strong>AWS Bedrock</strong> under a signed Business Associate Agreement (BAA).
        </p>
        <p style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.8)', lineHeight: 1.6, marginBottom: '10px', textAlign: 'left' }}>
          When you use Care Copilot, your messages and any health information you enter in the
          conversation are sent to this AI service to generate a response.
        </p>
        <p style={{ fontSize: '11.5px', color: 'rgba(255,255,255,0.6)', lineHeight: 1.6, marginBottom: '18px', textAlign: 'left' }}>
          This is also described in our{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#a5b4fc' }}>
            Privacy Policy
          </a>
          . You can decline and continue using the rest of AmyloidConnect without Care Copilot.
        </p>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={onDecline}
            style={{
              flex: 1, padding: '10px', background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: '10px',
              color: 'white', fontSize: '12px', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            No thanks
          </button>
          <button
            onClick={onAccept}
            style={{
              flex: 1, padding: '10px', background: 'linear-gradient(135deg, #667eea, #764ba2)',
              border: 'none', borderRadius: '10px', color: 'white', fontSize: '12px',
              fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            I Understand, Continue
          </button>
        </div>
      </div>
    </div>
  );
}